import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import BgOrnaments from "@/components/vu/BgOrnaments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PALETTE = ["#5B6CFF", "#DFFF3D", "#FF7A2E", "#2FA36B"];

const SEED = [
    { i: "LC", c: "#5B6CFF", q: "Our Google ranking went from page 3 to the map pack in about two months. Phones started ringing.", n: "Lucas Carter", r: "Owner, Carter HVAC", s: 5 },
    { i: "HR", c: "#DFFF3D", q: "The new site paid for itself within two months. Between the ads and the email follow-ups, we finally have a system instead of guessing.", n: "Hannah Reyes", r: "Operations Lead, Bloom Dental", s: 5 },
    { i: "MK", c: "#FF7A2E", q: "The AI voice agent alone was worth it. It books cleanings at 2am while we sleep — we haven't missed a call in six months.", n: "Maya Kwan", r: "Practice Manager, Northside Dental", s: 5 },
    { i: "CR", c: "#2FA36B", q: "Reporting is clear and the monthly call keeps us aligned. No more wondering where the ad budget went.", n: "Charlie Reid", r: "Founder, Reid & Co. Realty", s: 5 },
];

function initials(name) {
    return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "?";
}

export default function Testimonials() {
    const [items, setItems] = useState(SEED);
    const [idx, setIdx] = useState(1);
    const [form, setForm] = useState({ name: "", role: "", quote: "", rating: 0 });
    const [hoverRating, setHoverRating] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios.get(`${API}/reviews`)
            .then(({ data }) => {
                const fetched = data.map((rv, i) => ({
                    i: initials(rv.name),
                    c: PALETTE[i % PALETTE.length],
                    q: rv.quote,
                    n: rv.name,
                    r: rv.role || "",
                    s: rv.rating,
                }));
                setItems([...SEED, ...fetched]);
            })
            .catch((err) => console.error("Failed to load reviews", err));
    }, []);

    const t = items[idx] || items[0];

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.quote) { toast.error("Please add your name and a short review."); return; }
        if (!form.rating) { toast.error("Please select a star rating."); return; }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/reviews`, {
                name: form.name,
                role: form.role || undefined,
                quote: form.quote,
                rating: form.rating,
            });
            const newItem = { i: initials(data.name), c: PALETTE[items.length % PALETTE.length], q: data.quote, n: data.name, r: data.role || "", s: data.rating };
            setItems((prev) => [...prev, newItem]);
            setIdx(items.length); // jump carousel to the new review
            toast.success("Thanks for the review!");
            setForm({ name: "", role: "", quote: "", rating: 0 });
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section data-testid="testimonials-section" className="relative py-[110px] text-center border-t border-[#1F3A2B] overflow-hidden">
            <BgOrnaments variant="dots" />
            <div className="relative z-10 max-w-[1180px] mx-auto px-8">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-[#2A4A38] bg-[#152A20]/60 backdrop-blur rounded-full text-[11.5px] text-[#B8CFC0] mb-4">
                    <span className="w-[7px] h-[7px] rounded-full bg-[#DFFF3D]" /> Proven by Customer Success
                </div>
                <h2 className="font-display text-[2.4rem] mb-3 text-[#F5F0E8]">What our <span className="italic-serif text-[#DFFF3D]">clients say.</span></h2>
                <p className="text-[#7A9088] max-w-[500px] mx-auto mb-12">Our clients trust us to defend their digital presence, minimize downtime, and keep their businesses running.</p>

                <AnimatePresence mode="wait">
                    <motion.figure key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }} data-testid="testimonial-main" className="relative max-w-[720px] mx-auto rounded-[20px] p-10 bg-[#152A20] border border-[#1F3A2B] warm-glow">
                        <i className="fa-solid fa-quote-right absolute top-8 right-8 text-[#DFFF3D]/50 text-3xl" />
                        <div className="flex items-center justify-center gap-1 mb-4 text-[#DFFF3D]">
                            {"★".repeat(t.s).split("").map((s, i) => <span key={i}>{s}</span>)}
                            <span className="mono text-xs text-[#7A9088] ml-2">{t.s}.0/5</span>
                        </div>
                        <p className="text-[17px] leading-[1.7] text-[#F5F0E8] mb-8 max-w-[560px] mx-auto">"{t.q}"</p>
                        <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center font-display font-bold text-lg mb-3" style={{ background: t.c, color: t.c === "#DFFF3D" ? "#0E1F16" : "#F5F0E8" }}>{t.i}</div>
                        <b className="block text-[#F5F0E8] text-[15px]">{t.n}</b>
                        {t.r && <span className="text-[13px] text-[#7A9088]">{t.r}</span>}
                    </motion.figure>
                </AnimatePresence>

                <div className="flex justify-center gap-2.5 mt-10 mb-16">
                    <button data-testid="testimonial-prev" onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)} className="vu-btn w-10 h-10 rounded-full border border-[#1F3A2B] bg-[#152A20] flex items-center justify-center hover:border-[#DFFF3D] text-[#F5F0E8]"><ArrowLeft className="w-4 h-4" /></button>
                    <button data-testid="testimonial-next" onClick={() => setIdx((i) => (i + 1) % items.length)} className="vu-btn w-10 h-10 rounded-full border border-[#1F3A2B] bg-[#152A20] flex items-center justify-center hover:border-[#DFFF3D] text-[#F5F0E8]"><ArrowRight className="w-4 h-4" /></button>
                </div>

                {/* Leave a review */}
                <form onSubmit={submit} data-testid="review-form" className="max-w-[560px] mx-auto rounded-[20px] p-8 md:p-10 bg-[#152A20] border border-[#1F3A2B] text-left">
                    <h3 className="font-display text-[20px] text-[#F5F0E8] mb-1">Leave a review</h3>
                    <p className="text-[13px] text-[#7A9088] mb-6">Worked with us? Tell other business owners about it.</p>

                    <div className="mb-5">
                        <label className="mono text-[11px] text-[#7A9088] mb-2.5 block tracking-wider">YOUR RATING</label>
                        <div className="flex gap-1.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    data-testid={`review-input-rating-${n}`}
                                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                                    onMouseEnter={() => setHoverRating(n)}
                                    onMouseLeave={() => setHoverRating(0)}
                                    className="text-2xl leading-none transition-colors"
                                    style={{ color: n <= (hoverRating || form.rating) ? "#DFFF3D" : "#3A4F44" }}
                                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                                >★</button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                        <div>
                            <label className="mono text-[11px] text-[#7A9088] mb-2.5 block tracking-wider">NAME</label>
                            <input data-testid="review-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="w-full border border-[#1F3A2B] bg-[#0E1F16] text-[#F5F0E8] rounded-xl px-5 py-4 text-[15px] min-h-[56px] focus:outline-2 focus:outline-[#DFFF3D] placeholder:text-[#7A9088]" />
                        </div>
                        <div>
                            <label className="mono text-[11px] text-[#7A9088] mb-2.5 block tracking-wider">ROLE / BUSINESS (OPTIONAL)</label>
                            <input data-testid="review-input-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Owner, Acme HVAC" className="w-full border border-[#1F3A2B] bg-[#0E1F16] text-[#F5F0E8] rounded-xl px-5 py-4 text-[15px] min-h-[56px] focus:outline-2 focus:outline-[#DFFF3D] placeholder:text-[#7A9088]" />
                        </div>
                    </div>

                    <div className="mb-7">
                        <label className="mono text-[11px] text-[#7A9088] mb-2.5 block tracking-wider">YOUR REVIEW</label>
                        <textarea data-testid="review-input-quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={4} placeholder="Tell us how it went…" className="w-full border border-[#1F3A2B] bg-[#0E1F16] text-[#F5F0E8] rounded-xl px-5 py-4 text-[15px] min-h-[120px] leading-relaxed resize-y focus:outline-2 focus:outline-[#DFFF3D] placeholder:text-[#7A9088]" />
                    </div>

                    <button type="submit" disabled={submitting} data-testid="review-submit" className="vu-btn vu-cta-pulse inline-flex items-center gap-2 bg-[#DFFF3D] text-[#0E1F16] px-7 py-3.5 rounded-full font-semibold text-[14px] hover:bg-[#C7E82F] disabled:opacity-60 disabled:cursor-not-allowed">
                        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting…</> : <>Submit review</>}
                    </button>
                </form>
            </div>
        </section>
    );
}